// src/services/aeo/deliveryRules.ts
//
// The Delivery status rules, once. The backend owns them live (H4 status,
// H23 next step and who acts next, H17 drawer head and client line); the
// sample store runs this copy so the sample reads exactly as live will.
// Screens compute nothing: they print what the payload says.
//
// Fixed rules, no AI model. The same items always give the same group, next
// step, head and client line. Oura checks work; it never does it.

import type {
    ClientWord,
    Deliverable,
    DeliveryActor,
    DeliveryGroup,
    DeliveryItem,
    DeliveryPayload,
    ItemKind,
    ItemPage,
    ItemState,
    MainAction,
    NoteDraft,
    ReaderCheck,
} from "./deliveryTypes";
import { addDays, myDate, workingDaysBetween } from "./clientDocsRules";
import { createDoc, lineLabel, readThenShare } from "./lineWords";

/* ------------------------------------------------------------------------- *
 * The AI reader test (H38): one rule for which item it closes, shared by
 * Delivery, the Site screen's card and the sample store.
 * ------------------------------------------------------------------------- */

/** A3's AI readers item ends in this part, whatever its readerChecks hold. */
export const READERS_PART = ":tick-ai_readers";

/** The 3 readers the test runs as, by default. */
export const AI_READERS: ReadonlyArray<[ReaderCheck["reader"], string]> = [
    ["chatgpt", "GPTBot"],
    ["claude", "ClaudeBot"],
    ["perplexity", "PerplexityBot"],
];

/** The lines before the first test: each reader "not tested". */
export const UNTESTED_READERS: ReaderCheck[] = AI_READERS.map(([reader, agent]) => ({ reader, agent, robotsAllows: null, gotIn: null, wordsVisible: null, checkedAt: null }));

/** Whether an item is A3's AI readers item. */
export const isReadersItem = (item: Pick<DeliveryItem, "id"> | null | undefined): boolean => Boolean(item?.id.endsWith(READERS_PART));

/** The newest A3 whose window has started on `today`, and its AI readers item. */
export function readerItemOf(list: readonly Deliverable[], today: string): { d: Deliverable; item: DeliveryItem } | null {
    const a3 = list.filter((d) => d.code === "A3" && !!d.window && d.window.start <= today).sort((a, b) => b.month - a.month)[0];
    const item = a3?.items.find(isReadersItem);
    return a3 && item ? { d: a3, item } : null;
}

/* ------------------------------------------------------------------------- *
 * Words
 * ------------------------------------------------------------------------- */

export const KIND_LABEL: Record<ItemKind, string> = {
    check: "Oura checks it",
    record: "Oura checks it",
    page: "A page goes live",
    confirm: "We confirm it",
};

export const GROUP_TITLE: Record<DeliveryGroup, string> = {
    needs_us: "Needs us",
    waiting_client: "Waiting on client",
    checking: "Checking",
    done: "Done",
    later: "Later",
};

export const GROUP_ORDER: DeliveryGroup[] = ["needs_us", "waiting_client", "checking", "done", "later"];

/** The codes that read the key pages: until staff confirm them, their pinned action is to confirm. */
export const KEY_PAGE_CODES = new Set(["S1", "S3", "S6", "S7", "A2", "G2"]);

/**
 * Codes whose one check is counted in pages rather than items ("3 of 10 pages
 * pass"). `none` is what the line says when the check has NO pages, which is
 * not a count of zero — see `headOf` (#3999).
 */
const PAGE_COUNTED_CHECK: Record<string, { word: string; none: string }> = {
    S6: { word: "fixed", none: "No pages to fix" },
    S7: { word: "pass", none: "No slow pages" },
};

/** Codes whose items are pages ("5 of 8 pages live"). */
const PAGE_SLOT_CODES = new Set(["B1", "G1"]);
/** B1, G1, or a custom line that sells pages (H16): its items are page slots. */
const isPageDeliverable = (d: Pick<Deliverable, "code" | "items">) => PAGE_SLOT_CODES.has(d.code) || (d.items.length > 0 && d.items.every((i) => i.kind === "page"));

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "2026-10-16" -> "Fri 16 Oct". */
export function dayLabel(ymd: string | null | undefined): string {
    if (!ymd) return "";
    const [y, m, d] = ymd.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return "";
    const t = new Date(Date.UTC(y, m - 1, d));
    return `${DAYS[t.getUTCDay()]} ${d} ${MONTHS[m - 1]}`;
}

/** "2026-10-12" -> "12 Oct". */
export function shortDay(ymd: string | null | undefined): string {
    if (!ymd) return "";
    const [y, m, d] = ymd.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return "";
    return `${d} ${MONTHS[m - 1]}`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/* ------------------------------------------------------------------------- *
 * Items: the balance
 * ------------------------------------------------------------------------- */

const OPEN_PAGE = new Set<ItemPage["state"]>(["todo"]);
const WAITING_PAGE = new Set<ItemPage["state"]>(["waiting", "not_read"]);

/**
 * A check's balance, read from its pages: failing = fixed + waiting on scan +
 * to do. Pages closed by hand or with No action count as fixed. A balance
 * that does not add up would fail the read live; here it cannot, by
 * construction.
 */
export function checkCounts(pages: readonly ItemPage[]): { failing: number; fixed: number; waitingOnScan: number; todo: number } {
    let fixed = 0;
    let waitingOnScan = 0;
    let todo = 0;
    for (const p of pages) {
        if (OPEN_PAGE.has(p.state)) todo += 1;
        else if (WAITING_PAGE.has(p.state)) waitingOnScan += 1;
        else fixed += 1;
    }
    return { failing: pages.length, fixed, waitingOnScan, todo };
}

/** An item's state from its parts: a check from its pages; a slot, tick or record as stored. */
export function settleItem(item: DeliveryItem): DeliveryItem {
    if (item.doesNotApply) return { ...item, state: "done", waitingOn: null, cameBack: false };
    if (item.kind !== "check" || item.closedByHand) return item;
    const counts = checkCounts(item.pages);
    const cameBack = item.pages.some((p) => p.cameBack && p.state === "todo");
    let state: ItemState;
    if (counts.todo > 0) state = "todo";
    else if (counts.waitingOnScan > 0) state = "waiting";
    // No failing page is a clear only once Oura's scan has read the check this window.
    else if (item.pages.length === 0 && !item.readAt && !item.proof) state = "waiting";
    else state = "done";
    return { ...item, counts, state, cameBack, waitingOn: state === "waiting" ? "oura" : null };
}

/* ------------------------------------------------------------------------- *
 * Next step and who acts next (H23)
 * ------------------------------------------------------------------------- */

/** An item a person must move now: to do, or a client reply waiting on us. */
function needsUs(item: DeliveryItem): boolean {
    if (item.state === "todo") return true;
    return Boolean(item.clientRepliedAt) && item.state !== "done";
}

/**
 * The next-step rule, the same on every screen: the first open item that
 * needs us, in this order: a fix that came back, a client reply waiting on
 * us, the item due soonest, then the order on the plan.
 */
export function nextItemOf(items: readonly DeliveryItem[]): DeliveryItem | null {
    const open = items.map((item, index) => ({ item, index })).filter(({ item }) => needsUs(item));
    if (open.length === 0) return null;
    open.sort((a, b) => {
        const ca = a.item.cameBack ? 0 : 1;
        const cb = b.item.cameBack ? 0 : 1;
        if (ca !== cb) return ca - cb;
        const ra = a.item.clientRepliedAt ? 0 : 1;
        const rb = b.item.clientRepliedAt ? 0 : 1;
        if (ra !== rb) return ra - rb;
        const da = a.item.due ?? "9999";
        const db = b.item.due ?? "9999";
        if (da !== db) return da < db ? -1 : 1;
        return a.index - b.index;
    });
    return open[0].item;
}

/** The next step in words, by template. */
export function nextTextOf(item: DeliveryItem, docTitle: string | null): string {
    if (item.clientRepliedAt) {
        const topic = item.slot?.topic ? `: ${item.slot.topic.toLowerCase()}` : "";
        return `Reply in the ${docTitle ?? "document"}${topic}`;
    }
    if (item.nextText) return item.nextText;
    if (item.kind === "check") {
        const n = item.counts?.todo ?? item.pages.filter((p) => p.state === "todo").length;
        const came = item.pages.find((p) => p.cameBack && p.state === "todo");
        if (came) return `Fix ${item.label.toLowerCase()} again on ${came.url}`;
        return `${item.label} · ${plural(n, "page")}`;
    }
    if (item.kind === "page") return item.slot?.topic ? `Write the ${item.slot.topic.toLowerCase()} page` : "Add the next page";
    return item.label;
}

/** Work has begun: anything done or marked, a page in draft, or a client reply on it. */
function isStarted(items: readonly DeliveryItem[]): boolean {
    return items.some(
        (i) => i.state !== "todo" || i.markedFixedAt || i.clientRepliedAt || i.pages.some((p) => p.state !== "todo") || (i.kind === "page" && i.pages.length > 0),
    );
}

/* ------------------------------------------------------------------------- *
 * The group, first match wins (H4)
 * ------------------------------------------------------------------------- */

export function groupOf(d: Pick<Deliverable, "items" | "window">, today: string): DeliveryGroup {
    // No window: the plan has not started, so this is still ahead of us.
    if (!d.window || today < d.window.start) return "later";
    const open = d.items.filter((i) => i.state !== "done");
    if (open.length === 0) return "done";
    if (open.some(needsUs)) return "needs_us";
    if (open.some((i) => i.state === "waiting" && i.waitingOn === "client")) return "waiting_client";
    return "checking";
}

export function actorOf(
    d: Pick<Deliverable, "items" | "window" | "due">,
    group: DeliveryGroup,
    next: DeliveryItem | null,
    today: string,
    manager: string,
): DeliveryActor | null {
    if (group === "done" || group === "later") return null;
    // Late: past its due date and not Done, whoever holds it. A red date, never a group.
    // No due date is not an overdue one.
    const pastDue = !!d.due && d.due < today;
    if (group === "checking") return { who: "oura_check", when: "tonight", late: pastDue, cameBack: false };
    if (group === "waiting_client") {
        const since = d.items
            .filter((i) => i.state === "waiting" && i.waitingOn === "client")
            .map((i) => i.askedClientAt ?? today)
            .sort()[0];
        const days = workingDaysBetween(since.slice(0, 10), today);
        return { who: "client", when: plural(days, "day"), late: pastDue, cameBack: false };
    }
    const due = next?.due ?? d.due;
    const late = !!due && due < today;
    if (next?.cameBack) return { who: "person", name: manager, when: "came back", late: false, cameBack: true };
    if (late) return { who: "person", name: manager, when: dayLabel(due), late: true, cameBack: false };
    if (next?.clientRepliedAt || isStarted(d.items)) return { who: "person", name: manager, when: "today", late: false, cameBack: false };
    return { who: "person", name: manager, when: dayLabel(due), late: false, cameBack: false };
}

/** "Angel · today", "Client · 3 days", "Re-check tonight". */
export function actorText(a: DeliveryActor | null, fallbackName = "Angel"): string {
    if (!a) return "";
    // Oura's own row is a re-check of work a person did, never Oura doing the work.
    if (a.who === "oura_check") return `Re-check ${a.when}`;
    if (a.who === "client") return `Client · ${a.when}`;
    return `${a.name ?? fallbackName} · ${a.cameBack ? "came back" : a.when}`;
}

/* ------------------------------------------------------------------------- *
 * The drawer head and the client line (H17), by fixed template
 * ------------------------------------------------------------------------- */

/**
 * "3 working days", and "under 1 working day" for a wait that has not crossed
 * one — which is what a weekend, a holiday or an ask from this morning gives.
 * "0 working days" reads as a broken counter (#3999).
 *
 * The same rule the documents screens use (`waitText` / `waitingOnText` in
 * oura/docs/format.ts); repeated rather than imported because a service must
 * not reach up into a screen's words.
 */
export function workingDayText(days: number): string {
    return days <= 0 ? "under 1 working day" : plural(days, "working day");
}

function stateTail(items: readonly DeliveryItem[], today: string): string[] {
    const came = items.filter((i) => i.cameBack && i.state === "todo").length;
    const todo = items.filter((i) => i.state === "todo" && !i.cameBack).length;
    const scan = items.filter((i) => i.state === "waiting" && i.waitingOn !== "client").length;
    const client = items.filter((i) => i.state === "waiting" && i.waitingOn === "client");
    const parts: string[] = [];
    if (came) parts.push(`${came} came back`);
    if (todo) parts.push(`${todo} to do`);
    if (scan) parts.push(`${scan} waiting on scan`);
    if (client.length) {
        const since = client.map((i) => i.askedClientAt ?? today).sort()[0];
        const days = workingDaysBetween(since.slice(0, 10), today);
        parts.push(`${client.length} waiting on client, ${workingDayText(days)}`);
    }
    return parts;
}

/** The items a count reads: a check that does not apply is left out ("2 of 4", never "3 of 5"), as H17 counts. */
function counted(items: readonly DeliveryItem[]): DeliveryItem[] {
    const kept = items.filter((i) => !i.doesNotApply);
    return kept.length ? kept : items.slice();
}

export function headOf(d: Pick<Deliverable, "code" | "items" | "outlet">, group: DeliveryGroup, doneAt: string | null, today: string): string {
    const items = counted(d.items);
    const done = items.filter((i) => i.state === "done").length;
    const pageCheck = PAGE_COUNTED_CHECK[d.code];
    if (group === "done") {
        if (isPageDeliverable(d)) return `Done ${shortDay(doneAt)} · ${items.length} of ${items.length} pages live`;
        if (items.every((i) => i.kind === "check")) {
            // A deliverable the server closed (carried from Fixes as done, or
            // closed by hand) can hold checks Oura's scan has not read this
            // window, which are never an invented clear. Say so, rather than
            // "Done · 0 of 4 clear", which reads as a contradiction.
            if (done === items.length) return `Done ${shortDay(doneAt)} · ${done} of ${items.length} clear`;
            const unread = items.length - done;
            return `Done ${shortDay(doneAt)} · ${done > 0 ? `${done} of ${items.length} clear, ` : ""}${plural(unread, "check")} read at the next scan`;
        }
        return `Done ${shortDay(doneAt)} · ${done} of ${items.length} done`;
    }
    if (pageCheck && items.length === 1 && items[0].kind === "check") {
        const it = items[0];
        const c = checkCounts(it.pages);
        const total = it.pages.length;
        // NO PAGES IS NOT A COUNT OF ZERO. A check's `pages` are the ones
        // FAILING it, so an empty list means one of two different things, and
        // "0 of 0 pages fixed" said neither (#3999):
        //
        //   read, and nothing was failing  -> there is nothing to do
        //   never read                     -> we do not know yet
        //
        // A check that has been read and passed is `done` — the done branch
        // above counts exactly those as "clear" — so the state tells them
        // apart without inventing a measurement.
        if (total === 0) return it.state === "done" ? pageCheck.none : "Waiting on scan";
        const head = `${c.fixed} of ${total} pages ${pageCheck.word}`;
        const parts = [head];
        if (c.todo) parts.push(`${c.todo} to do`);
        if (c.waitingOnScan) parts.push(`${c.waitingOnScan} waiting on scan`);
        return parts.join(" · ");
    }
    if (isPageDeliverable(d)) {
        const live = items.filter((i) => i.state === "done").length;
        return [`${live} of ${items.length} pages live`, ...stateTail(items, today)].join(" · ");
    }
    // S4 with 2 or more outlets: one item per outlet.
    if (items.length > 1 && items.every((i) => i.id.includes(":outlet-"))) {
        return [`${done} of ${items.length} outlets confirmed`, ...stateTail(items, today)].join(" · ");
    }
    return [`${done} of ${items.length} done`, ...stateTail(items, today)].join(" · ");
}

export function clientWordOf(group: DeliveryGroup, items: readonly DeliveryItem[]): ClientWord {
    if (group === "done") return "Done";
    if (group === "waiting_client") return "Needs you";
    if (group === "later") return "Not started";
    return isStarted(items) ? "In progress" : "Not started";
}

/**
 * The client line: the state word, then the count in plain words. Never
 * claims, came back, dismissals, staff names or open items.
 */
export function clientLineOf(
    d: Pick<Deliverable, "code" | "items" | "window" | "month" | "proofLine" | "doneAt">,
    word: ClientWord,
    today: string,
): string {
    const items = counted(d.items);
    const done = items.filter((i) => i.state === "done").length;
    if (word === "Done") return `Done ${shortDay(d.doneAt)}.${d.proofLine ? ` ${d.proofLine}` : ""}`;
    if (word === "Needs you") {
        const ask = items.find((i) => i.state === "waiting" && i.waitingOn === "client")?.clientAsk;
        return `Needs you. ${ask ?? "We sent you a question."}`;
    }
    if (word === "Not started") {
        if (!d.window) return "Not started.";
        if (today < d.window.start) return `Not started. Month ${d.month} starts ${dayLabel(d.window.start)}.`;
        if (d.window && today > d.window.end) return `Not started. It was planned for month ${d.month}.`;
        return "Not started. We start this month.";
    }
    const waiting = items.some((i) => i.state === "waiting" && i.waitingOn !== "client");
    const tail = waiting ? " We confirm the rest at our next check." : "";
    if (isPageDeliverable(d)) {
        const left = items.length - done;
        return `In progress. ${done} of ${items.length} pages are live.${left ? (done === 0 ? ` We are writing all ${left}.` : ` We are writing the other ${left}.`) : ""}`;
    }
    const pageCheck = PAGE_COUNTED_CHECK[d.code];
    if (pageCheck && items.length === 1) {
        const c = checkCounts(items[0].pages);
        if (d.code === "S7") return `In progress. ${c.fixed} of your ${items[0].pages.length} key pages load fast on a phone.${tail}`;
        return `In progress. ${c.fixed} of ${items[0].pages.length} key pages are linked.${tail}`;
    }
    const scope = items.some((i) => i.scope === "key_pages") ? " on your 10 key pages" : "";
    return `In progress. ${done} of ${items.length} done${scope}.${tail}`;
}

/* ------------------------------------------------------------------------- *
 * The pinned main action, first match wins
 * ------------------------------------------------------------------------- */

export function mainActionOf(
    d: Pick<Deliverable, "code" | "items" | "document" | "outlet" | "docStep">,
    group: DeliveryGroup,
    next: DeliveryItem | null,
    keyPagesConfirmed: boolean,
    docsWrite = false,
): MainAction {
    if (group !== "done" && group !== "later" && !keyPagesConfirmed && KEY_PAGE_CODES.has(d.code)) {
        return { kind: "confirm_key_pages", label: "Confirm the 10 key pages" };
    }
    if (group === "needs_us" && next?.clientRepliedAt && d.document) return { kind: "open_doc", label: "Open the doc", docId: d.document.docId, itemId: next.id };
    // Row 4, contract v1.3 (H31): a doc not made or not shared is the next step and the drawer's main
    // button in every open group, never a control under History. Behind the Docs write switch; a
    // covering doc that is not shared is read, then shared, never created a second time.
    const step = docsWrite && group !== "done" && group !== "later" ? d.docStep : null;
    if (step) {
        return step.kind === "share" && step.docId
            ? { kind: "create_doc", label: "Read it, then share", docId: step.docId, codes: step.codes }
            : { kind: "create_doc", label: "Create the doc", docId: null, docType: step.docType, codes: step.codes };
    }
    if (group === "needs_us" && next) {
        // The Live since reply is posted from its own box in the drawer, where it is read first; the foot opens the doc it goes to.
        if (next.liveSinceDraft && !next.liveSinceDraft.postedAt) return { kind: "open_doc", label: "Open the doc", docId: next.liveSinceDraft.docId, itemId: next.id };
        if (next.kind === "check") return { kind: "mark_fixed", label: `Mark ${next.label.toLowerCase()} fixed`, itemId: next.id };
        if (next.kind === "page") return { kind: "add_live_page", label: "Add a live page", itemId: next.id };
        if (next.kind === "confirm") {
            const who = next.outlet ? ` ${next.outlet}` : "";
            return { kind: "tick", label: `Tick${who}, with a link or a note`, itemId: next.id };
        }
        if (next.kind === "record") {
            const to = recordView(next);
            return { kind: "go_to", label: next.staffDo ?? "Open it", itemId: next.id, to };
        }
    }
    if (group === "waiting_client") {
        if (d.document) return { kind: "open_doc", label: "Open the doc", docId: d.document.docId };
        return { kind: "go_to", label: "Open Client", to: "reviews" };
    }
    if (group === "checking") {
        // Only pages Oura cannot read are left, and nothing is marked: staff decide, with proof.
        const stuck = d.items.filter((i) => i.state === "waiting" && i.waitingOn !== "client");
        const blocked = stuck.find((i) => i.kind === "check" && i.pages.length > 0 && i.pages.every((p) => p.state === "not_read" || p.state === "done" || p.state === "closed_by_hand" || p.state === "no_action"));
        if (blocked && stuck.every((i) => !i.pages.some((p) => p.state === "waiting"))) {
            return { kind: "close_by_hand", label: `Close ${blocked.label.toLowerCase()} by hand`, itemId: blocked.id };
        }
        return { kind: "check_now", label: "Check now" };
    }
    return { kind: "next_deliverable", label: "Next deliverable" };
}

/**
 * Where a record's work happens, as a staff view of this business: the
 * server's `goTo` when it sends one, else by the record's name (H4's ids): a
 * report (uploaded on Client), the AI reading (Visibility), a connection
 * (Connectors), a kept suggestion (Site), a run (Rerun assessment sits on
 * every page).
 */
export function recordView(item: Pick<DeliveryItem, "id" | "goTo">): string {
    if (item.goTo) return item.goTo;
    const part = item.id.slice(item.id.indexOf(":") + 1);
    if (part === "record-report") return "reviews";
    if (part === "record-reading") return "questions";
    if (part === "record-connected" || part === "record-first_data") return "connectors";
    if (part === "record-placeholder_pages") return "setup";
    return "dashboard";
}

/* ------------------------------------------------------------------------- *
 * One deliverable, computed
 * ------------------------------------------------------------------------- */

export interface ComputeCtx {
    today: string;
    manager: string;
    keyPagesConfirmed: boolean;
    /** The Docs write switch (DOCS_WRITE_ON): offers create_doc (v1.3). Unset = off. */
    docsWrite?: boolean;
}

/** Fills every computed field from the items. `raw` carries the stored parts. */
export function computeDeliverable(input: Deliverable, ctx: ComputeCtx): Deliverable {
    // The line's name as it reads on screen: no "(2–3)" count from the quotation.
    const raw = { ...input, label: lineLabel(input.label) };
    // A month that has not started has no scan to read: its items stay to do.
    const started = !!raw.window && ctx.today >= raw.window.start;
    const settled = started ? raw.items.map(settleItem) : raw.items.map((i) => ({ ...i, state: "todo" as const, waitingOn: null, cameBack: false }));
    // The AI reader test reads robots.txt as each reader does: all 3 in, after the robots fix, is the
    // re-check the robots line waits on, so it closes on that proof too.
    const readerPass = settled.find((i) => i.readerChecks?.length && i.readerChecks.every((r) => r.robotsAllows === true && r.gotIn === true && r.wordsVisible === true));
    const testedAt = readerPass?.readerChecks?.[0]?.checkedAt ?? null;
    const items = settled.map((i) =>
        testedAt && i.checkKey === "robots_missing" && i.state === "waiting" && (!i.markedFixedAt || i.markedFixedAt <= testedAt)
            ? {
                  ...i,
                  state: "done" as const,
                  waitingOn: null,
                  pages: i.pages.map((p) => (p.state === "waiting" ? { ...p, state: "done" as const, detail: "Confirmed by the AI reader test" } : p)),
                  proof: i.proof ?? { kind: "scan" as const, text: `robots.txt lets all 3 AI readers in, tested ${shortDay(myDate(testedAt))}`, at: testedAt },
              }
            : i,
    );
    const base = { ...raw, items };
    const group = groupOf(base, ctx.today);
    // A Live since reply drafted and not posted is a step of its own: raised, never left in a folded Done.
    const unposted = items.find((i) => i.liveSinceDraft && !i.liveSinceDraft.postedAt) ?? null;
    const next = group === "needs_us" ? (unposted ?? nextItemOf(items)) : null;
    const lastDone = items
        .map((i) => i.proof?.at ?? i.markedFixedAt ?? null)
        .filter((x): x is string => Boolean(x))
        .sort()
        .pop();
    const doneAt = group === "done" ? raw.doneAt ?? (lastDone ? myDate(lastDone) : null) ?? (raw.window && raw.window.end < ctx.today ? raw.window.end : ctx.today) : null;
    const withDone = { ...base, doneAt };
    const word = clientWordOf(group, items);
    // Passed before any work was logged: never Done on its own (Jack, 22 Oct 2026).
    if (raw.passedWithoutWork && group === "done") {
        return {
            ...base,
            group: "needs_us",
            doneAt: null,
            next: null,
            actor: null,
            head: "Passed with no work logged. Add what we did, or mark it already fine",
            clientWord: "In progress",
            clientLine: "In progress. We confirm this at our next check.",
            mainAction: mainActionOf(base, "needs_us", null, ctx.keyPagesConfirmed, ctx.docsWrite ?? false),
        };
    }
    return {
        ...withDone,
        group,
        next: next
            ? {
                  itemId: next.id,
                  // The doc comes first when it is not made or not shared: the row says so.
                  text:
                      ctx.docsWrite && raw.docStep
                          ? raw.docStep.kind === "share" && raw.docStep.docId
                              ? readThenShare(raw.docStep.docType)
                              : createDoc(raw.docStep.docType)
                          : next === unposted
                            ? "Post the Live since reply in the doc"
                            : nextTextOf(next, raw.document?.title ?? null),
              }
            : null,
        actor: actorOf(base, group, next, ctx.today, ctx.manager),
        head: headOf(base, group, doneAt, ctx.today),
        clientWord: word,
        clientLine: clientLineOf(withDone, word, ctx.today),
        mainAction: mainActionOf(base, group, next, ctx.keyPagesConfirmed, ctx.docsWrite ?? false),
    };
}

/**
 * "N of M done", the same number on every screen: every deliverable due this
 * month, plus earlier ones still open.
 */
export function doneCountOf(deliverables: readonly Deliverable[], month: number): { done: number; total: number } {
    const counted = deliverables.filter((d) => !d.reportedByHand && (d.month === month || (d.month < month && d.group !== "done")));
    return { done: counted.filter((d) => d.group === "done").length, total: counted.length };
}

/**
 * The Friday note draft, by H17's template (the server's route is the live
 * source; this is the sample's): what finished since the last note or in 7
 * days, the Latest actions (none on the sample), then the next 2 steps by
 * rank, then what waits on the client. Contract labels, first letter lower
 * case unless a name or an acronym. No staff name, claim or item list.
 */
export function noteDraftOf(p: DeliveryPayload, today: string, lastNoteAt: string | null = null): NoteDraft {
    const weekAgo = addDays(today, -7);
    const last = lastNoteAt ? lastNoteAt.slice(0, 10) : null;
    const since = last && last > weekAgo ? last : weekAgo;
    const lower = (s: string) => (/^(AI|Google|SEO|ChatGPT)\b/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1));
    const list = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
    const planIndex = new Map(p.deliverables.map((d, i): [string, number] => [d.key, i]));
    const finished = p.deliverables.filter((d) => d.group === "done" && d.doneAt && d.doneAt.slice(0, 10) >= since && d.doneAt.slice(0, 10) <= today);
    const next = p.deliverables
        .filter((d) => d.group === "needs_us" && d.next && !finished.includes(d))
        .sort((a, b) => todayRank(a, planIndex.get(a.key) ?? 99) - todayRank(b, planIndex.get(b.key) ?? 99))
        .slice(0, 2);
    const waiting = p.deliverables.filter((d) => d.group === "waiting_client");
    const parts: string[] = [];
    if (finished.length) parts.push(`This week we finished ${list(finished.map((d) => lower(d.label)))}.`);
    else parts.push("This week we kept working on your plan.");
    if (next.length) parts.push(`Next we are on ${list(next.map((d) => lower(d.label)))}.`);
    if (waiting.length) parts.push(`We are waiting on you for ${list(waiting.map((d) => lower(d.label)))}.`);
    return {
        since,
        until: today,
        draft: parts.join(" "),
        facts: {
            finished: finished.map((d) => ({ key: d.key, label: d.label, doneAt: d.doneAt!.slice(0, 10) })),
            actions: [],
            next: next.map((d) => ({ key: d.key, label: d.label })),
            waiting: waiting.map((d) => ({ key: d.key, label: d.label, ask: d.items.find((i) => i.state === "waiting" && i.waitingOn === "client")?.clientAsk ?? null })),
        },
        lastNote: null,
    };
}

/** The rank the Today list sorts by: came back, client reply, due soonest, plan order. */
export function todayRank(d: Deliverable, planIndex: number): number {
    const next = d.items.find((i) => i.id === d.next?.itemId);
    const came = next?.cameBack ? 0 : 1;
    const reply = next?.clientRepliedAt ? 0 : 1;
    // 99999999 sorts a dateless deliverable last, exactly as the `||` already
    // did for an unreadable date. The date itself can be ABSENT: a plan that
    // has not started carries null on every deliverable, and this read
    // `.replace` straight off it.
    const due = Number((next?.due ?? d.due ?? "").replace(/-/g, "")) || 99999999;
    return came * 1e12 + reply * 1e11 + due * 100 + Math.min(planIndex, 99);
}
